import { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle, Clock, ArrowRight } from "lucide-react";

const C = {
  bg: "#FAFAF8", white: "#FFFFFF", navy: "#1B2A4A",
  gold: "#C9A452", goldLight: "rgba(201,164,82,0.12)", goldBorder: "rgba(201,164,82,0.3)",
  text: "#111827", muted: "#6B7280", border: "#E8E3D8", section: "#F5F2EC",
};

const PHONE = "0749124282";
const WHATSAPP = "33749124282";
const EMAIL = "skoolformation93@hotmail.com";

const CMA_DATES = [
  { session: "Session Été", examen: "30 juin 2026", cloture: "22 mai 2026", statut: "Ouvert" },
  { session: "Session Automne", examen: "19 novembre 2026", cloture: "8 octobre 2026", statut: "À venir" },
  { session: "Session Hiver", examen: "28 janvier 2027", cloture: "17 décembre 2026", statut: "À venir" },
];

type FormData = { nom: string; prenom: string; email: string; telephone: string; formation: string; financement: string; message: string; };
const INIT: FormData = { nom: "", prenom: "", email: "", telephone: "", formation: "", financement: "", message: "" };

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "12px 16px", borderRadius: 10,
  background: C.white, border: `1px solid ${C.border}`,
  color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box",
  transition: "border-color 0.2s",
};
const labelStyle: React.CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 7 };

export default function Contact() {
  const [form, setForm] = useState<FormData>(INIT);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const submit = (e: React.FormEvent) => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSent(true); }, 900); };

  return (
    <div style={{ background: C.bg }}>
      {/* Header */}
      <div style={{ background: C.navy, padding: "64px 24px 56px", textAlign: "center" }}>
        <div style={{ display: "inline-block", padding: "4px 14px", borderRadius: 20, background: C.goldLight, border: `1px solid ${C.goldBorder}`, color: C.gold, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>
          Contact & Inscription
        </div>
        <h1 style={{ fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 700, color: "#fff", marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>
          Contactez-nous
        </h1>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 15, maxWidth: 460, margin: "0 auto" }}>
          Formulaire d'inscription, question sur les formations — réponse sous 24h.
        </p>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 48 }}>

          {/* ─ Infos ─ */}
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, marginBottom: 20, fontFamily: "'Playfair Display', serif" }}>Nos coordonnées</h2>
            {[
              { icon: MapPin, title: "Adresse", lines: ["8 Rue Layla Zana", "93000 Bobigny"], link: "https://maps.google.com/?q=8+Rue+Layla+Zana+93000+Bobigny", ll: "Voir sur la carte" },
              { icon: Phone, title: "Téléphone", lines: ["07 49 12 42 82"], link: `tel:${PHONE}`, ll: "Appeler maintenant" },
              { icon: Mail, title: "Email", lines: [EMAIL], link: `mailto:${EMAIL}`, ll: "Envoyer un email" },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} style={{ borderRadius: 14, padding: 18, background: C.white, border: `1px solid ${C.border}`, marginBottom: 12, display: "flex", gap: 14 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: C.section, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon size={17} color={C.navy} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 3 }}>{c.title}</div>
                    {c.lines.map((l) => <div key={l} style={{ color: C.muted, fontSize: 13, marginBottom: 2 }}>{l}</div>)}
                    <a href={c.link} target={c.link.startsWith("https") ? "_blank" : undefined} rel="noreferrer" style={{ color: C.gold, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
                      {c.ll} →
                    </a>
                  </div>
                </div>
              );
            })}

            {/* WhatsApp */}
            <a href={`https://wa.me/${WHATSAPP}?text=Bonjour%20SKOOL%20FORMATION%2C%20je%20souhaite%20des%20informations.`} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 18px", borderRadius: 14, background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.2)", textDecoration: "none", marginBottom: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center" }}><MessageCircle size={17} color="#fff" /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>WhatsApp — Réponse rapide</div>
                <div style={{ color: "#15803d", fontSize: 12 }}>07 49 12 42 82 · Réponse sous 24h</div>
              </div>
              <ArrowRight size={14} color="#16a34a" />
            </a>

            {/* Horaires */}
            <div style={{ borderRadius: 14, padding: 18, background: C.white, border: `1px solid ${C.border}`, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Clock size={15} color={C.navy} />
                <span style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>Horaires</span>
              </div>
              {[["Lun–Ven", "09h00 – 18h00"], ["Samedi", "09h00 – 13h00"], ["Dimanche", "Fermé"]].map(([j, h]) => (
                <div key={j} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: C.muted }}>{j}</span>
                  <span style={{ fontSize: 13, color: h === "Fermé" ? C.muted : C.navy, fontWeight: 600 }}>{h}</span>
                </div>
              ))}
            </div>

            {/* CMA mini */}
            <div style={{ borderRadius: 14, padding: 18, background: C.section, border: `1px solid ${C.border}` }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: C.navy, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Prochaines sessions CMA IDF</div>
              {CMA_DATES.map((d) => (
                <div key={d.session} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.navy }}>{d.examen}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: d.statut === "Ouvert" ? "rgba(22,163,74,0.1)" : C.goldLight, color: d.statut === "Ouvert" ? "#15803d" : "#92700a" }}>{d.statut}</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>Clôture : {d.cloture}</div>
                </div>
              ))}
            </div>

            {/* SIREN */}
            <div style={{ marginTop: 12, padding: "12px 16px", borderRadius: 10, background: C.white, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.8 }}>
                <strong style={{ color: C.navy }}>SKOOL FORMATION SASU</strong><br />
                SIREN : 999 479 785 — RCS Bobigny<br />
                8 Rue Layla Zana, 93000 Bobigny<br />
                Présidente : Sonia
              </div>
            </div>
          </div>

          {/* ─ Formulaire ─ */}
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, marginBottom: 20, fontFamily: "'Playfair Display', serif" }}>Formulaire d'inscription / Contact</h2>
            {sent ? (
              <div style={{ borderRadius: 16, padding: 48, textAlign: "center", background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.2)" }}>
                <CheckCircle size={56} color="#16a34a" style={{ margin: "0 auto 20px" }} />
                <h3 style={{ fontSize: 22, fontWeight: 700, color: C.navy, marginBottom: 10 }}>Demande envoyée !</h3>
                <p style={{ color: C.muted, fontSize: 15, marginBottom: 24 }}>
                  Merci <strong style={{ color: C.navy }}>{form.prenom} {form.nom}</strong> !<br />
                  Nous vous répondrons sous 24h à <strong style={{ color: C.gold }}>{form.email}</strong>.
                </p>
                <button onClick={() => { setSent(false); setForm(INIT); }} style={{ padding: "12px 24px", borderRadius: 10, background: C.navy, color: "#fff", border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                  Nouvelle demande
                </button>
              </div>
            ) : (
              <form onSubmit={submit} style={{ background: C.white, borderRadius: 20, border: `1px solid ${C.border}`, padding: 32, boxShadow: "0 4px 20px rgba(27,42,74,0.06)" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <div><label style={labelStyle}>Nom *</label><input name="nom" value={form.nom} onChange={handle} required placeholder="Votre nom" style={inputStyle} /></div>
                  <div><label style={labelStyle}>Prénom *</label><input name="prenom" value={form.prenom} onChange={handle} required placeholder="Votre prénom" style={inputStyle} /></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <div><label style={labelStyle}>Email *</label><input name="email" type="email" value={form.email} onChange={handle} required placeholder="email@exemple.fr" style={inputStyle} /></div>
                  <div><label style={labelStyle}>Téléphone</label><input name="telephone" value={form.telephone} onChange={handle} placeholder="07 XX XX XX XX" style={inputStyle} /></div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Formation souhaitée *</label>
                  <select name="formation" value={form.formation} onChange={handle} required style={{ ...inputStyle, appearance: "none" as const }}>
                    <option value="">Choisissez une formation</option>
                    <option value="VTC">Formation VTC — 699 € TTC</option>
                    <option value="Passerelle VTC→Taxi">Passerelle VTC → Taxi (14h) — 499 € TTC</option>
                    <option value="Formation Taxi">Formation Taxi — 799 € TTC</option>
                    <option value="Session féminine">Session exclusivement féminine</option>
                    <option value="Renseignements">Renseignements uniquement</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Mode de financement</label>
                  <select name="financement" value={form.financement} onChange={handle} style={{ ...inputStyle, appearance: "none" as const }}>
                    <option value="">Sélectionnez...</option>
                    
                    
                    
                    <option value="Stripe">Paiement en ligne (Stripe)</option>
                    <option value="Virement">Virement bancaire</option>
                    <option value="Autre">Autre / À définir</option>
                  </select>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>Message / Questions</label>
                  <textarea name="message" value={form.message} onChange={handle} rows={4} placeholder="Décrivez votre situation, vos disponibilités, vos questions..." style={{ ...inputStyle, resize: "vertical" as const }} />
                </div>
                <button type="submit" disabled={loading} style={{ width: "100%", padding: "15px", borderRadius: 10, background: C.navy, color: "#fff", fontWeight: 700, fontSize: 15, border: "none", cursor: loading ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: "0 6px 20px rgba(27,42,74,0.22)", opacity: loading ? 0.7 : 1 }}>
                  <Send size={16} /> {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                </button>
                <p style={{ textAlign: "center", color: C.muted, fontSize: 11, marginTop: 12 }}>Données confidentielles — SKOOL FORMATION SASU</p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div style={{ borderTop: `1px solid ${C.border}` }}>
        <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2621.5!2d2.4268!3d48.9088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s8%20Rue%20Layla%20Zana%2C%2093000%20Bobigny!5e0!3m2!1sfr!2sfr!4v1234567890" width="100%" height="280" style={{ border: "none", display: "block" }} allowFullScreen loading="lazy" title="SKOOL FORMATION — Bobigny" />
      </div>

      <style>{`
        input:focus, textarea:focus, select:focus { border-color: ${C.navy} !important; box-shadow: 0 0 0 3px rgba(27,42,74,0.08); }
        select option { background: #fff; }
        @media (max-width: 768px) { .contact-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}
